import type { Status } from '../../../components/primitives/types'

/**
 * Lifecycle states an ingest job row can be in.
 *
 * - `uploading` is the initial state set by `/begin` while the browser
 *   is still streaming bytes via `xhr.upload`. Rows in this state read
 *   their progress from the redux upload-progress slice rather than
 *   the static `stateToProgress` table below.
 * - `uploaded` is a short handoff after unzip lands in S3
 *   (`ingest-document-unzips/{orgId}/{jobId}/...`) before the job moves to
 *   `queued` for the unstructured-ingest agent. Not terminal.
 * - `classifying` / `classified` / `extracting` / `ingested` /
 *   `transforming` / `transformed` remain for historical and non-agent
 *   job rows; new unstructured agent runs use `queued` → `processing`
 *   → `done|error`.
 * - `awaiting_user` parks an agent run for human-in-the-loop questions.
 *   The row keeps polling until the run resumes to `processing` then
 *   `done` (or `error`).
 * - `awaiting_review` holds a mapped tabular run with flagged pages: nothing
 *   is committed until someone reviews or excludes each one on the run page.
 *   A run can sit here for days, so the row does not poll.
 * - `awaiting_turn` holds a mapped tabular intake run whose rows are ready
 *   while an earlier file from its intake is unsettled. It commits on its own
 *   once that file is committed or discarded, which can take as long as a
 *   review, so the row does not poll either.
 * - `cancelling` is set the moment the user clicks Stop. The row
 *   keeps polling so it transitions naturally to `cancelled` once
 *   the in-process worker observes its AbortSignal at the next
 *   stage boundary.
 * - `cancelled` is the terminal state for a user-stopped ingest.
 *   Partial graph data is not rolled back; re-uploading is the
 *   supported recovery path.
 */
export type IngestJobState =
  | 'uploading'
  | 'queued'
  | 'processing'
  | 'uploaded'
  | 'classifying'
  | 'classified'
  | 'extracting'
  | 'ingested'
  | 'awaiting_user'
  | 'awaiting_rebuild'
  | 'awaiting_review'
  | 'awaiting_turn'
  | 'transforming'
  | 'transformed'
  | 'done'
  | 'error'
  | 'skipped'
  | 'cancelling'
  | 'cancelled'

export const statusToPill: Record<IngestJobState, Status> = {
  uploading: 'info',
  queued: 'info',
  processing: 'info',
  // Transient post-unzip handoff before the agent is queued.
  uploaded: 'info',
  classifying: 'info',
  classified: 'info',
  extracting: 'info',
  ingested: 'info',
  awaiting_user: 'warn',
  awaiting_rebuild: 'warn',
  awaiting_review: 'warn',
  awaiting_turn: 'info',
  transforming: 'info',
  transformed: 'run',
  done: 'run',
  error: 'down',
  skipped: 'idle',
  cancelling: 'warn',
  cancelled: 'idle'
}

export const statusToBar: Record<IngestJobState, Status> = {
  uploading: 'warn',
  queued: 'warn',
  processing: 'warn',
  uploaded: 'warn',
  classifying: 'warn',
  classified: 'warn',
  extracting: 'warn',
  ingested: 'warn',
  awaiting_user: 'warn',
  awaiting_rebuild: 'warn',
  awaiting_review: 'warn',
  awaiting_turn: 'warn',
  transforming: 'warn',
  transformed: 'run',
  done: 'run',
  error: 'down',
  skipped: 'idle',
  cancelling: 'warn',
  cancelled: 'idle'
}

export const statusToLabel: Record<IngestJobState, string> = {
  uploading: 'uploading',
  queued: 'queued',
  processing: 'running',
  uploaded: 'uploaded',
  classifying: 'classifying',
  classified: 'classified',
  extracting: 'extracting',
  ingested: 'ingested',
  awaiting_user: 'review needed',
  awaiting_rebuild: 'config update needed',
  awaiting_review: 'needs review',
  awaiting_turn: 'waiting',
  transforming: 'transforming',
  transformed: 'complete',
  done: 'complete',
  error: 'failed',
  skipped: 'skipped',
  cancelling: 'cancelling',
  cancelled: 'cancelled'
}

// `uploading` rows render with a live bytes-transferred percentage from
// `selectUploadProgress`; this default (2) is only used during the brief
// moment between job creation and the first `xhr.upload.onprogress` tick.
export const stateToProgress: Record<IngestJobState, number> = {
  uploading: 2,
  queued: 5,
  processing: 30,
  // Brief post-unzip handoff before the agent queue claim.
  uploaded: 20,
  classifying: 55,
  classified: 65,
  extracting: 75,
  ingested: 85,
  awaiting_user: 88,
  awaiting_rebuild: 60,
  awaiting_review: 90,
  awaiting_turn: 95,
  transforming: 92,
  transformed: 100,
  done: 100,
  error: 100,
  skipped: 100,
  cancelling: 100,
  cancelled: 100
}

/**
 * Scale a raw XHR byte-progress percentage (0-100) into the upload portion
 * of the ingest bar (`stateToProgress.uploading` → `stateToProgress.processing`).
 *
 * Without scaling, the live byte-progress would race to 100% while the row
 * is still in `uploading`, then snap backwards to 30% (`processing`) the
 * moment the upload lands and the server flips the job to its first
 * post-upload state. Compressing the upload phase into the first ~30% of
 * the bar keeps it monotonic across that handoff: the bar slides from 2%
 * (start of upload) to 30% (end of upload) to 30% (start of processing).
 */
export function scaleLiveUploadPct(livePct: number): number {
  const floor = stateToProgress.uploading
  const ceiling = stateToProgress.processing
  const clamped = Math.max(0, Math.min(100, livePct))
  return Math.round(floor + (clamped / 100) * (ceiling - floor))
}
